import React, { Component } from 'react';
import { Header, List, Table } from 'semantic-ui-react';

export default class ExperienceLogPage extends Component {
  render() {
    return (
      <div>
        <Header as="h1" content="Experience log" />
        <p>
          Throughout the semester, you will be keeping a log of your experiences with
          cybersecurity and/or privacy outside of the immediate assignments in this class
          and relating those experiences to the concepts and research papers we discussed
          in class.
        </p>

        <p>
          The goal of these experience logs is to give you a chance to reflect on how
          cybersecurity and privacy manifest in your own day-to-day experiences with
          computing and technology. A secondary goal is to give you an opportunity to
          explore the concepts you’ve learned in class to your real-life experiences,
          and to give you some freedom in doing so.
        </p>

        <p>
          Accordingly, “experiences”, for this assignment, are loosely defined. They can
          be either active or passive. Passive experiences are things that happen to you
          (e.g., a friend asks you about how you make your passwords, your data is
          unwittingly compromised in a breach), while active experiences are those that you
          specifically design (e.g., you interview a few friends about updating their phone
          software). Here are some other example experiences you could write about for your
          experience logs:
        </p>

        <List bulleted>
          <List.Item>
            An analysis of an article you read about cybersecurity and/or privacy (not an assigned reading)
          </List.Item>
          <List.Item>
            A conversation you had with someone else (outside of this class) about cybersecurity and/or privacy
          </List.Item>
          <List.Item>
            Results from a questionnaire about cybersecurity behaviors you sent out to some friends.
          </List.Item>
          <List.Item>
            A decision you made that might have privacy and/or cybersecurity consequences and the difficulties you faced
          </List.Item>
          <List.Item>
            An observation of someone else’s cybersecurity and/or privacy decisions or behaviors
          </List.Item>
          <List.Item>
            Creating a low-fidelity prototype of an idea you have
          </List.Item>
        </List>

        <p>
          If you’re unsure if what you want to write about qualifies, speak to Sauvik after class or send him
          an email. The only strict constraint on content is that whatever you write about must be from
          <i>that two week period</i>.
        </p>

        <p>
          Logs that are particularly well done will be featured on the homepage of our class
          Medium publication. You should take al ook at these for examples of well done log
          entries once a few have been selected.
        </p>

        <Header as="h2" content="Experience log entries" />
        <p>
          Each log entry should be about 500 words and consist of the following three parts:
        </p>

        <List bulleted>
          <List.Item>
            <i>Description</i>: A detailed description of the “experience”. For example,
            <List bulleted>
              <List.Item>If passive, what was the context and why does it stand out?</List.Item>
              <List.Item>If active, what did you do and why?</List.Item>
            </List>
          </List.Item>
          <List.Item>
            <i>Relation</i>: A statement of how your "experience" relates to the concepts we’ve learned in class. For example,
            <List bulleted>
              <List.Item>What theories, concepts and/or methods that we discussed did you draw from?</List.Item>
              <List.Item>What in-class activities or discussions inspired you?</List.Item>
            </List>
          </List.Item>
          <List.Item>
            <i>Implication</i>: The upshot of your experience. For example,
            <List bulleted>
              <List.Item>Does your experience surface a new research question previously unanswered in the literature?</List.Item>
              <List.Item>Do you have new ideas for systems you might want to develop that could help end-users with a task?</List.Item>
            </List>
          </List.Item>
        </List>

        <p>
          Note that the bulleted list under each part (i.e., description, relation and implication) are just example
          questions you could answer. Not all of those questions will be relevant for all experiences, and
          the set of questions are certainly not exhaustive.
        </p>

        <Header as="h2" content="Class Medium publication" />
        <p>
          I have created a <a href="https://medium.com/gt-ups-course">Medium publication</a> for
          the class where each of you will post your experience logs. This way, your ideas
          and writing can impact not only your fellow classmates, but the broader community
          of researchers and practitioners of usable privacy and security. It should also
          provide you with writing practice that you can later include in a portfolio.
        </p>

        <p>
          Before the first Experience Log entry is due, please create a Medium account
          and send Sauvik a message with your account name.
        </p>

        <Header as="h2" content="Deliverable" />
        <p>
          You will write three experience logs, due on 1/17, 2/14, and 3/14. Each log should be
          ~500 words. Post your logs to the <a href="https://medium.com/gt-ups-course"
          target="_blank">class Medium publication</a>.
        </p>

        <p>
          Also submit <i>the link</i> to your log entry on Canvas so that it is available for peer grading.
        </p>

        <Header as="h2" content="Grading others' experience logs" />
        <p>
          After you submit an entry, you will be assigned up to three of your classmates’ experience logs
          on Canvas. Read their entries and write a comment on their Medium post. These peer grades and comments
          will be due one week after the corresponding log entry is due, not including Spring Break, (i.e., 1/24,
          2/21 and 3/28).
        </p>

        <p>
          In your comments, attempt to foster a discussion with the author. Did their experience
          inspire an idea? Can you think of interesting modifications?
        </p>

        <p>
          Finally, enter a grade for this log entry on Canvas. Use the rubric below. In general,
          the majority of log entries should get a 'check' in all categories. with a few exemplary
          entries getting a 'check-plus' and a few weaker ones getting a 'check-minus'. Add up the
          points earned across all three dimensions when entering your final score. This should
          be a number between 24 - 30.
        </p>

        <p>Peer grading counts towards your overall class participation.</p>

        <Header as="h2" content="Grading" />
        <p>
          Your log entries will be peer-graded on a "check", "check plus", "check minus" system along
          the three dimensions stated above: description, relation and implication. We will use the
          following rubric:
        </p>

        <Table definition>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              <Table.HeaderCell>Check Minus(8 pts)</Table.HeaderCell>
              <Table.HeaderCell>Check(9 pts)</Table.HeaderCell>
              <Table.HeaderCell>Check Plus(10 pts)</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            <Table.Row>
              <Table.Cell>Description</Table.Cell>
              <Table.Cell>
                The experience was not well described or motivated. It was unclear what was done and why.
              </Table.Cell>
              <Table.Cell>
                The experience was well described and motivated. It was clear what was done and why, but required a little effort.
              </Table.Cell>
              <Table.Cell>
                The experience very well described and motivated. It was easy to follow what was done and why.
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell>Relation</Table.Cell>
              <Table.Cell>
                Attempts to tie the experience back to in-class readings, discussions and activities
                were non-existent, vague, inaccurate and/or poorly described.
              </Table.Cell>
              <Table.Cell>
                The experience was adequately tied back to in-class readings, discussions and/or activities.
                There were a few relevant citations. The tie-ins were mostly accurate but somewhat shallow.
              </Table.Cell>
              <Table.Cell>
                The experience very well tied back back to in-class readings, discussions and/or activities.
                There were many relevant citations. The tie-ins were accurate and engaged deeply with the
                relevant related work.
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell>Implication</Table.Cell>
              <Table.Cell>
                The stated implications of the experience were non-existent, shallow or unconvincing.
              </Table.Cell>
              <Table.Cell>
                The stated implications of the experience were convincing, clear and could be useful
                for future inquiries.
              </Table.Cell>
              <Table.Cell>
                The stated implications of the experience were convincing, clear and inspiring. The
                experience could be a promising first step towards a deeper original research contribution.
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
    )
  }
}


// WEBPACK FOOTER //
// ./src/pages/experience-log-page.js